'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { useTranslations } from 'use-intl';
import { Locale } from '@/project.config';
import useGA4WidgetImpressions from '@/hooks/useGA4WidgetImpressions';
import { pushSelectItem } from '@/lib/analytics/dataLayer';
import { mappedProductToGA4Item } from '@/lib/analytics/ga4Items';
import { ProductTileV2Role } from '@/components/molecules/product-tile-v2/types';
import AddToPurchaseListDrawer from '@/components/organisms/add-to-purchase-list-drawer';
import FeaturedProductsCarousel from '@/components/organisms/featured-products-carousel';
import { FeaturedProductEntry } from '@/components/organisms/featured-products-carousel/types';
import { buildFeaturedEntries } from '@/components/organisms/featured-products-carousel/utils/build-featured-entries';
import toast from '@/components/atoms/toaster/helpers/toast';
import useAccount from '@/lib/hooks/useAccount';
import useAccountRoles from '@/lib/hooks/useAccountRoles';
import useShoppingListAccess from '@/lib/hooks/useShoppingListAccess';
import useCart from '@/lib/hooks/useCart';
import usePurchaseLists from '@/lib/hooks/usePurchaseLists';
import { addToPurchaseListWithFeedback } from '@/lib/tastics/product-list/components/product-list-view-model/helpers/addToPurchaseListWithFeedback';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { mapPurchaseList } from '@/utils/mappers/map-purchase-list';
import { SparePartsCarouselProps } from './types';

// EWM-433 — shared "Spare Parts" carousel used by the PDP (single product's
// spares) and the cart page (spares across all line items). Renders the SAME
// role-aware FeaturedProductsCarousel cards as the homepage Featured Products.
// The server resolves + caches price/stock (getCtSnapshotsForSection) and hands
// them down as ctBySku; this component merges (buildFeaturedEntries) + renders.
const SparePartsCarousel = ({ items, ctBySku, heading, listId = 'widget_spare-parts' }: SparePartsCarouselProps) => {
  const translate = useTranslations();

  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();
  const { account } = useAccount();
  const { permissions } = useAccountRoles(selectedBusinessUnit?.key);
  // EWM-653 — add-to-list visibility comes from the single source of truth, not
  // re-derived here. `role` is still used for cart/sign-in tile states below.
  const { canUsePurchaseLists } = useShoppingListAccess(selectedBusinessUnit?.key);
  const { addItem, cart } = useCart(selectedBusinessUnit?.key, selectedStore?.key);

  // EWM-643 — real locale for the per-country removed-from-sale (`GO`) flag.
  const { locale } = useParams<{ locale: Locale }>();

  const entries = useMemo(() => buildFeaturedEntries(items, ctBySku, { cart, locale }), [items, ctBySku, cart, locale]);

  // EWM-654 — the capability gate now lives inside usePurchaseLists (keyed on the
  // centralized useShoppingListAccess), so we no longer re-derive it here. We keep
  // `enabled` only for the carousel-specific condition the hook can't know: this
  // component is mounted on the cart even when there are no spare parts
  // (`entries.length === 0` returns null below) and the hook sits above that
  // early-return, so without this an empty cart would still fire the query.
  const {
    purchaseLists: rawWishlists,
    addToWishlists,
    createPurchaseList,
  } = usePurchaseLists(selectedStore?.key, { enabled: entries.length > 0 });

  const [addingEntry, setAddingEntry] = useState<FeaturedProductEntry | null>(null);

  const role: ProductTileV2Role = !account ? 'guest' : permissions.UpdateMyCarts ? 'master' : 'view-only';

  const headline = heading ?? translate('pdp.section-spare-parts');
  const listName = headline;

  const ga4Items = useMemo(
    () => entries.map((entry, i) => mappedProductToGA4Item(entry.item, i + 1, listId, listName)),
    [entries, listId, listName],
  );
  const { containerRef } = useGA4WidgetImpressions({ items: ga4Items, listId, listName });

  const lists = useMemo(
    () =>
      (rawWishlists?.items ?? []).map((wishlist) => {
        const mapped = mapPurchaseList(wishlist);
        return { id: mapped.id, name: mapped.name, itemCount: mapped.items.length };
      }),
    [rawWishlists],
  );

  const handleAddToCart = useCallback(
    async (sku: string, qty: number) => {
      const res = await addItem([{ sku, count: qty }]);
      if (!res.success) {
        const message = res.message || translate('common.something-went-wrong');
        toast.error(message);
        return;
      }
      toast.success(translate('cart.item-added'));
    },
    [addItem, translate],
  );

  const handleSubmitPurchaseList = useCallback(
    async (selectedListIds: string[]) => {
      if (!addingEntry) return;
      await addToPurchaseListWithFeedback({
        addToWishlists,
        translate,
        sku: addingEntry.item.sku ?? '',
        wishlistIds: selectedListIds,
      });
      setAddingEntry(null);
    },
    [addingEntry, addToWishlists, translate],
  );

  const handleCreateList = useCallback(
    async (name: string) => {
      const created = await createPurchaseList({ name, store: selectedStore });
      return created?.wishlistId ? { id: created.wishlistId, name: created.name ?? name, itemCount: 0 } : null;
    },
    [createPurchaseList, selectedStore],
  );

  if (entries.length === 0) return null;

  return (
    <>
      <div ref={containerRef as React.RefCallback<HTMLDivElement>}>
        <FeaturedProductsCarousel
          heading={headline}
          role={role}
          canAddToPurchaseList={canUsePurchaseLists}
          backOrdersAllowed={selectedBusinessUnit?.backOrdersAllowed ?? false}
          products={entries}
          showActions
          showRrp
          onAddToCart={handleAddToCart}
          onAddToPurchaseList={setAddingEntry}
          onProductClick={(sku, index) => {
            const entry = entries.find((e) => e.item.sku === sku);
            if (!entry) return;
            pushSelectItem({
              item_list_id: listId,
              item_list_name: listName,
              item: mappedProductToGA4Item(entry.item, index, listId, listName),
            });
          }}
        />
      </div>

      {addingEntry && (
        <AddToPurchaseListDrawer
          isOpen={!!addingEntry}
          product={{ name: addingEntry.item.name, sku: addingEntry.item.sku ?? '' }}
          lists={lists}
          onCreateList={handleCreateList}
          onSubmit={handleSubmitPurchaseList}
          onClose={() => setAddingEntry(null)}
        />
      )}
    </>
  );
};

export default SparePartsCarousel;
